import React from "react";

export interface OverviewFieldTile {
    // Fields drawn by the same tile share a key, so a tile covering several fields - period, amount, address -
    // is drawn once, in the position of whichever of its fields comes first.
    key: string;
    // A thunk rather than a node so a tile is only built when the template actually kept its field; several of
    // them index into data that is empty when the field is gone.
    render: () => React.ReactNode;
}

// A field can draw several tiles - suppliers also draws secondary suppliers - and each has to be its own entry, since
// rows are packed by counting entries.
export type OverviewFieldTiles = Record<number, OverviewFieldTile | Array<OverviewFieldTile> | undefined>;

// The backend owns which tiles a row has and what order they come in, so the page renders the list it is given
// rather than filtering a list of its own.
export function getOverviewFieldTiles(fields: Array<number> | undefined, tiles: OverviewFieldTiles): Array<React.ReactNode> {
    const drawn = new Set<string>();
    const result: Array<React.ReactNode> = [];

    for (const field of fields ?? []) {
        for (const tile of [tiles[field] ?? []].flat()) {
            if (drawn.has(tile.key)) {
                continue;
            }

            drawn.add(tile.key);

            // A tile can still decline to draw - tags hang off a permission the template knows nothing about - and a
            // row left with nothing in it should collapse rather than show an empty strip.
            const tileContent = tile.render();
            if (!tileContent) {
                continue;
            }

            result.push(<React.Fragment key={tile.key}>{tileContent}</React.Fragment>);
        }
    }

    return result;
}
